// Cookie consent and the tools it gates. Nothing that sets non-essential
// cookies (Google Analytics, PostHog, Meta Pixel) loads before the visitor opts in.
// Bump VERSION whenever a new category or tool is added so everyone is asked again.

const STORAGE_KEY = 'ip9-cookie-consent';
const VERSION = 2;
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
const OPEN_EVENT = 'ip9:open-cookie-settings';

const GA_ID = 'G-8S92FZRZ9Z';
const POSTHOG_KEY = 'phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE';
const POSTHOG_HOST = 'https://us.i.posthog.com';
const META_PIXEL_ID = '1147767504718642';

export function getConsent() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (!raw || raw.v !== VERSION || Date.now() - Date.parse(raw.date) > MAX_AGE_MS) return null;
    return raw;
  } catch {
    return null;
  }
}

export function saveConsent({ analytics, marketing }) {
  const value = { v: VERSION, analytics: Boolean(analytics), marketing: Boolean(marketing), date: new Date().toISOString() };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* storage unavailable: the choice applies to this page view only */
  }
  applyConsent(value);
  return value;
}

export function applyConsent(consent) {
  if (consent && consent.analytics) enableAnalytics();
  else disableAnalytics();
  if (consent && consent.marketing) enableMarketing();
  else disableMarketing();
}

// The SPA changes routes without page loads; the initial PageView is sent on init.
export function trackPageView() {
  if (pixelLoaded && pixelGranted && window.fbq) window.fbq('track', 'PageView');
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function onOpenCookieSettings(handler) {
  window.addEventListener(OPEN_EVENT, handler);
  return () => window.removeEventListener(OPEN_EVENT, handler);
}

let gaLoaded = false;
let posthogLoaded = false;
let pixelLoaded = false;
let pixelGranted = false;

function enableAnalytics() {
  window[`ga-disable-${GA_ID}`] = false;
  if (!gaLoaded) {
    gaLoaded = true;
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_ID, { anonymize_ip: true });
  }

  if (!posthogLoaded) {
    posthogLoaded = true;
    loadPosthog();
    window.posthog.init(POSTHOG_KEY, { api_host: POSTHOG_HOST, person_profiles: 'identified_only' });
  } else if (window.posthog && window.posthog.opt_in_capturing) {
    window.posthog.opt_in_capturing();
  }
}

function disableAnalytics() {
  window[`ga-disable-${GA_ID}`] = true;
  if (window.posthog && window.posthog.opt_out_capturing) window.posthog.opt_out_capturing();
  clearCookies(/^(_ga|_gid|_gat|ph_)/);
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith('ph_'))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

function enableMarketing() {
  if (!pixelLoaded) {
    pixelLoaded = true;
    loadMetaPixel();
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
  } else if (!pixelGranted) {
    window.fbq('consent', 'grant');
  }
  pixelGranted = true;
}

function disableMarketing() {
  if (pixelLoaded && window.fbq) window.fbq('consent', 'revoke');
  pixelGranted = false;
  clearCookies(/^(_fbp|_fbc)$/);
}

function clearCookies(pattern) {
  const host = window.location.hostname;
  const parts = host.split('.');
  const domains = ['', host];
  for (let i = 1; i < parts.length - 1; i++) domains.push(`.${parts.slice(i).join('.')}`);
  document.cookie.split(';').forEach((c) => {
    const name = c.split('=')[0].trim();
    if (!pattern.test(name)) return;
    domains.forEach((d) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ''}`;
    });
  });
}

// Official Meta Pixel base code (queues calls until fbevents.js arrives).
/* eslint-disable */
function loadMetaPixel() {
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
}
/* eslint-enable */

// Official PostHog loader stub (queues calls until array.js arrives).
/* eslint-disable */
function loadPosthog() {
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split('.');
          2 == o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            });
        }
        ((p = t.createElement('script')).type = 'text/javascript'),
          (p.crossOrigin = 'anonymous'),
          (p.async = !0),
          (p.src = s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js'),
          (r = t.getElementsByTagName('script')[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = 'posthog'),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = 'posthog';
              return 'posthog' !== a && (e += '.' + a), t || (e += ' (stub)'), e;
            },
            u.people.toString = function () {
              return u.toString(1) + '.people (stub)';
            },
            o =
              'init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug getPageViewId'.split(
                ' ',
              ),
            n = 0;
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);
}
/* eslint-enable */
